import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../layouts/DashboardLayout';
import PageHeader from '../components/layout/PageHeader';
import { statsApi, productsApi } from '../lib/api';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { MagnifyingGlass, Funnel, Download, CircleNotch, MapPin, ArrowCounterClockwise } from '@phosphor-icons/react';
import { formatDateTime } from '../lib/utils';
import { Label } from '../components/ui/label';

function scanLocationLabel(scan) {
  if (scan.latitude != null && scan.longitude != null) {
    return `${Number(scan.latitude).toFixed(4)}, ${Number(scan.longitude).toFixed(4)}`;
  }
  return scan.location || '-';
}

function openScanOnMap(scan) {
  if (scan.latitude == null || scan.longitude == null) return;
  window.open(
    `https://www.google.com/maps/dir/?api=1&destination=${scan.latitude},${scan.longitude}`,
    '_blank',
    'noopener,noreferrer',
  );
}

export default function ReportsPage() {
  const { t } = useTranslation();
  const [scans, setScans] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [filters, setFilters] = useState({ product_id: 'all', scan_type: 'all', start_date: '', end_date: '' });

  const loadData = useCallback(async () => {
    try {
      const [scansRes, productsRes] = await Promise.all([statsApi.scans({}), productsApi.list()]);
      setScans(scansRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error('Failed to load report data:', error);
      toast.error('Failed to load data');
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleFilter = async () => {
    if (filters.start_date && filters.end_date && filters.end_date < filters.start_date) {
      toast.error('End date cannot be earlier than start date');
      return;
    }
    setLoading(true);
    try {
      const params = {};
      if (filters.product_id !== 'all') params.product_id = filters.product_id;
      if (filters.scan_type !== 'all') params.scan_type = filters.scan_type;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      const response = await statsApi.scans(params);
      setScans(response.data);
    } catch (error) {
      console.error('Failed to filter scans:', error);
      toast.error('Failed to apply filters');
    }
    finally { setLoading(false); }
  };

  const handleClearFilters = async () => {
    setFilters({ product_id: 'all', scan_type: 'all', start_date: '', end_date: '' });
    setLoading(true);
    try {
      const response = await statsApi.scans({});
      setScans(response.data);
    } catch (error) {
      toast.error('Failed to reset filters');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    const csvContent = [
      ['QR ID', 'Scan Type', 'User', 'Location', 'Timestamp (IST)'].join(','),
      ...scans.map((s) =>
        [
          s.qr_id,
          s.scan_type,
          s.user_name || 'Consumer',
          `"${(scanLocationLabel(s) || '-').replace(/"/g, '""')}"`,
          `"${formatDateTime(s.timestamp)}"`,
        ].join(','),
      ),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scan-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="reports-page">
        <PageHeader
          title={t('reports.title')}
          description={t('reports.viewExport')}
          actions={(
            <Button onClick={handleExport} variant="outline" data-testid="export-report-btn">
              <Download className="mr-2 h-4 w-4" /> Export CSV
            </Button>
          )}
        />

        <Card className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} shadow-sm`}>
          <CardHeader className="pb-3">
            <CardTitle className={`text-sm font-medium flex items-center gap-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
              <Funnel className="w-4 h-4" /> {t('reports.filters')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <div className="space-y-1.5">
                <Label className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>{t('reports.product', { defaultValue: 'Product' })}</Label>
                <Select value={filters.product_id} onValueChange={v => setFilters({ ...filters, product_id: v })}>
                  <SelectTrigger className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''} data-testid="filter-product"><SelectValue placeholder={t('reports.allProducts')} /></SelectTrigger>
                  <SelectContent className={isDark ? 'bg-zinc-800 border-zinc-700' : ''}>
                    <SelectItem value="all">{t('reports.allProducts')}</SelectItem>
                    {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>{t('common.type')}</Label>
                <Select value={filters.scan_type} onValueChange={v => setFilters({ ...filters, scan_type: v })}>
                  <SelectTrigger className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''} data-testid="filter-scan-type"><SelectValue placeholder={t('reports.allTypes')} /></SelectTrigger>
                  <SelectContent className={isDark ? 'bg-zinc-800 border-zinc-700' : ''}>
                    <SelectItem value="all">{t('reports.allTypes')}</SelectItem>
                    <SelectItem value="internal">{t('reports.internal')}</SelectItem>
                    <SelectItem value="consumer">{t('reports.consumer')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>{t('reports.fromDate', { defaultValue: 'From date' })}</Label>
                <Input type="date" value={filters.start_date}
                  max={filters.end_date || undefined}
                  onChange={e => setFilters({ ...filters, start_date: e.target.value })}
                  className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''} data-testid="filter-start-date" />
              </div>
              <div className="space-y-1.5">
                <Label className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>{t('reports.toDate', { defaultValue: 'To date' })}</Label>
                <Input type="date" value={filters.end_date}
                  min={filters.start_date || undefined}
                  onChange={e => setFilters({ ...filters, end_date: e.target.value })}
                  className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''} data-testid="filter-end-date" />
              </div>
              <div className="flex items-end gap-2 md:col-span-2">
                <Button onClick={handleFilter} className="bg-brand-600 hover:bg-brand-700 text-white flex-1" data-testid="apply-filters-btn">
                  <MagnifyingGlass className="w-4 h-4 mr-2" /> {t('reports.apply', { defaultValue: 'Apply' })}
                </Button>
                <Button onClick={handleClearFilters} variant="outline" className={isDark ? 'border-zinc-700 text-zinc-300' : ''} data-testid="clear-filters-btn">
                  <ArrowCounterClockwise className="w-4 h-4 mr-2" /> {t('reports.clear', { defaultValue: 'Clear' })}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} shadow-sm`}>
          <CardHeader className="pb-3">
            <CardTitle className={`text-lg font-heading ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>
              {t('reports.scanHistory', { defaultValue: 'Scan History' })} ({scans.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className={`${isDark ? 'border-zinc-800' : 'border-zinc-200'} hover:bg-transparent`}>
                  {[t('reports.qrId'), t('common.type'), t('reports.user'), t('reports.location'), t('reports.notes'), t('reports.timestamp')].map(h => (
                    <TableHead key={h} className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8"><CircleNotch className="w-6 h-6 animate-spin mx-auto text-brand-500" /></TableCell></TableRow>
                ) : scans.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className={`text-center py-8 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>No scans recorded</TableCell></TableRow>
                ) : scans.map((scan, i) => (
                  <TableRow key={i} className={isDark ? 'border-zinc-800 hover:bg-zinc-800/50' : 'border-zinc-200 hover:bg-zinc-50'}>
                    <TableCell className={`font-mono text-sm ${isDark ? 'text-zinc-300' : 'text-zinc-600'}`}>{scan.qr_id?.substring(0, 8)}...</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${scan.scan_type === 'internal'
                        ? (isDark ? 'bg-brand-500/10 text-brand-400 border-brand-500/30' : 'bg-brand-50 text-brand-600 border-brand-200')
                        : (isDark ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' : 'bg-green-50 text-green-600 border-green-200')
                      }`}>{scan.scan_type}</span>
                    </TableCell>
                    <TableCell className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>{scan.user_name || 'Consumer'}</TableCell>
                    <TableCell className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>
                      {scan.latitude != null && scan.longitude != null ? (
                        <button
                          type="button"
                          onClick={() => openScanOnMap(scan)}
                          className="inline-flex items-center gap-1 text-left text-brand-600 hover:underline"
                          title="Open on map"
                        >
                          <MapPin className="h-3.5 w-3.5 shrink-0" />
                          {scanLocationLabel(scan)}
                          {scan.location_source === 'ip_geo' ? (
                            <span className="text-[10px] text-amber-600">IP</span>
                          ) : null}
                        </button>
                      ) : (
                        scanLocationLabel(scan)
                      )}
                    </TableCell>
                    <TableCell className={`max-w-[200px] truncate ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>{scan.notes || '-'}</TableCell>
                    <TableCell className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>{formatDateTime(scan.timestamp)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
